import ColorMixer from './colorMix/colorMixer'
import { Tag, UITag } from '../types/tag'
import { WordTagStyles } from '../models/word'

function getTagsIndices(tags: Tag[]) {
  return tags.flatMap((tag: Tag) => {
    if (tag.wordIndices) {
      return tag.wordIndices.map(i => ({
        ...tag,
        startWordIndex: i.startWordIndex,
        endWordIndex: i.endWordIndex
      }))
    }

    return {
      ...tag,
      startWordIndex: tag.startWordIndex,
      endWordIndex: tag.endWordIndex
    }
  })
}

function getTagsIndicesForWord(tags: Tag[], wordIndex: number) {
  return getTagsIndices(tags).filter(tag => {
    return tag.startWordIndex <= wordIndex && wordIndex <= tag.endWordIndex
  })
}

function twoTagsStartFromWord(tags: Tag[], wordIndex: number): boolean {
  const [firstTag, secondTag] = tags

  const firstTagUsages = getTagsIndicesForWord([firstTag], wordIndex)
  const secondTagUsages = getTagsIndicesForWord([secondTag], wordIndex)

  //Returns true if two tag usages fully overlaps
  return firstTagUsages.some(f => {
    return secondTagUsages.some(
      s =>
        f.startWordIndex === s.startWordIndex &&
        f.endWordIndex === s.endWordIndex
    )
  })
}

function tagStartsFromWord(wordIndex: number, tag: Tag): boolean {
  if (tag.wordIndices) {
    return tag.wordIndices.some(i => i.startWordIndex === wordIndex)
  } else {
    return tag.startWordIndex === wordIndex
  }
}

function tagEndsOnWord(wordIndex: number, tag: Tag): boolean {
  if (tag.wordIndices) {
    return tag.wordIndices.some(i => i.endWordIndex === wordIndex)
  } else {
    return tag.endWordIndex === wordIndex
  }
}

function getTagsStartIndices(tags: Tag[]) {
  return tags.flatMap((tag: Tag) => {
    if (tag.wordIndices) {
      return tag.wordIndices.map(i => i.startWordIndex).flat()
    } else {
      return tag.startWordIndex
    }
  })
}

function getTagsEndIndices(tags: Tag[]) {
  return tags.flatMap((tag: Tag) => {
    if (tag.wordIndices) {
      return tag.wordIndices.map(i => i.endWordIndex).flat()
    } else {
      return tag.endWordIndex
    }
  })
}

function singleWordWithThreeOrMoreTags(
  wordIndex: number,
  tags: Tag[]
): boolean {
  if (tags.length < 3) {
    return false
  }

  const startIndices: number[] = getTagsStartIndices(tags)
  const endIndices: number[] = getTagsEndIndices(tags)

  return (
    startIndices.some(t => t === wordIndex) &&
    endIndices.some(t => t === wordIndex)
  )
  // return wordIndex === Math.max(...startIndices) && wordIndex === Math.min(...endIndices)
}

function singleWordWithTwoTags(wordIndex: number, tags: Tag[]): boolean {
  const singleWordTags = getTagsIndicesForWord(tags, wordIndex).filter(
    tag => tag.startWordIndex === tag.endWordIndex
  )

  return singleWordTags.length > 1
}

function wordHasThreeOrMoreTagsAndStartsOne(wordIndex: number, tags: Tag[]) {
  if (tags.length < 3) {
    return false
  }
  const startIndices = getTagsStartIndices(tags)
  return startIndices.some(t => t === wordIndex)
  // return wordIndex === Math.max(...startIndices)
}

function wordHasThreeOrMoreTagsAndEndsOne(wordIndex: number, tags: Tag[]) {
  if (tags.length < 3) {
    return false
  }
  const endIndices = getTagsEndIndices(tags)
  return endIndices.some(t => t === wordIndex)
  // return wordIndex === Math.min(...endIndices)
}

function styleTwoTags(index: number, wordTags: UITag[]) {
  const styles: WordTagStyles = {}

  // If 2 tags start in this word
  if (singleWordWithTwoTags(index, wordTags)) {
    styles.isBackground = true
    styles.color = ColorMixer.string2hex('white')
    styles.isFullyBordered = true
    return styles
  }

  if (twoTagsStartFromWord(wordTags, index)) {
    styles.color = ColorMixer.string2hex('white')
    styles.isBackground = true

    if (tagStartsFromWord(index, wordTags[0])) {
      styles.isRoundedLeft = true
      styles.hasTopBottomBorderSpace = true
      return styles
    }

    if (tagEndsOnWord(index, wordTags[1])) {
      styles.isRoundedRight = true
      return styles
    }

    styles.hasTopBottomBorder = true
    styles.hasTopBottomBorderSpace = true

    return styles
  } else if (
    tagEndsOnWord(index, wordTags[0]) ||
    tagEndsOnWord(index, wordTags[1])
  ) {
    styles.color = ColorMixer.mix(
      ColorMixer.string2hex(wordTags[0].color ? wordTags[0].color : 'white'),
      ColorMixer.string2hex(wordTags[1].color ? wordTags[1].color : 'white')
    )
    styles.isBackground = true
    styles.isRoundedRight = true
    return styles
  } else if (
    tagStartsFromWord(index, wordTags[0]) ||
    tagStartsFromWord(index, wordTags[1])
  ) {
    styles.color = ColorMixer.mix(
      ColorMixer.string2hex(wordTags[0].color ? wordTags[0].color : 'white'),
      ColorMixer.string2hex(wordTags[1].color ? wordTags[1].color : 'white')
    )
    styles.isBackground = true
    styles.isRoundedLeft = true
    return styles
  }
  styles.hasTopBottomBorder = true
  styles.hasTopBottomBorderSpace = true
  styles.isBackground = true
  styles.color = ColorMixer.mix(
    ColorMixer.string2hex(wordTags[0].color ? wordTags[0].color : 'white'),
    ColorMixer.string2hex(wordTags[1].color ? wordTags[1].color : 'white')
  )
  return styles
}

function styleThreeAndMoreTags(wordTags: UITag[], index: number) {
  const styles: WordTagStyles = {}

  styles.isBackground = true
  styles.color = ColorMixer.mix(
    ColorMixer.string2hex(wordTags[0].color ? wordTags[0].color : 'white'),
    ColorMixer.string2hex(wordTags[1].color ? wordTags[1].color : 'white')
  )

  if (singleWordWithThreeOrMoreTags(index, wordTags)) {
    styles.isFullyBordered = true
    return styles
  }

  if (wordHasThreeOrMoreTagsAndStartsOne(index, wordTags)) {
    styles.isRoundedLeft = true
    styles.hasTopBottomBorderSpace = true
    return styles
  }

  if (wordHasThreeOrMoreTagsAndEndsOne(index, wordTags)) {
    styles.isRoundedRight = true
    return styles
  }

  styles.hasTopBottomBorder = true
  styles.hasTopBottomBorderSpace = true
  return styles
}

function styleSingleTag(wordTags: UITag[]) {
  return {
    isBackground: true,
    color: wordTags[0].color ? ColorMixer.string2hex(wordTags[0].color) : ''
  }
}

export class TagStyler {
  static computeStyles(wordTags: UITag[], index: number) {
    switch (wordTags.length) {
      case 0:
        return {}
      case 1:
        return styleSingleTag(wordTags)
      case 2:
        return styleTwoTags(index, wordTags)
      default:
        return styleThreeAndMoreTags(wordTags, index)
    }
  }
}
